import { utilsApp } from "../utils-app_v2.js";
import {
  NAME_MAX_LENGTH,
  SECQA_MAX_LENGTH,
  SECQA_MIN_LENGTH,
  BLURB_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  BANNED_PASSWORDS,
  IDENTITY_ROLES,
  WRITE_SHARDS,
  IDENTITY_TYPES,
  RESERVED_NAMES,
  FORBIDDEN_CHARS,
  COMPUTER_NAME,
  COMPUTER_USERID,
} from "../constants_v2.js";
import { dateTimeUtils } from "../dateTimeUtils_v2.js";
import { matchService } from "../matches/matchService_v2.js";
import { colors } from "../colors_v2.js";
import { isAnimationModeValid } from "../game/animations_v2.js";
import { subscriptionManager } from "../subscriptionManager_v2.js";
import { idem } from "../idem.mjs";
import { app } from "../app_v2.js";
import {
  findBannedWords,
  isNamePartWellFormed,
  isSearchWellFormed,
  hasControlChars,
} from "../textFilter_v2.js";
import { escapeHtml } from "../escapeHtml_v2.js";

const identityState = {
  cache: [], // all identities this session has encountered
  playingAsName: "notSet", // could be the logged on user or a temp identity
  refreshPromise: null,
  sessionInvalidated: false,
};

/**
 * @typedef {object} identity
 * @property {string} userID
 * @property {string} name - The name of the identity with the userID in parens after it: Jay Garing (VG9RTGD) or Player One (Temp_GTDC6YP)
 * @property {email} email - The email of the identity, only required for an account. 
 * @property {string} type "account" or "temp" // used as pk for lots of different sk's
 * @property {string} writeShard one of 26 values :"a" , "b", "c" and so on. Used for write sharding the partition key.
 * @property {string} allItems the literal string "allItems" the purpose is so I can get leaderboard stuff back in sorted order. Probably have parition throttling at some point
 * @property {number} totalWins - the number of matches won
 * @property {number} totalMatches - the number of matches with participation role of player
 * @property {number} totalWatches - The number of matches with participation role of player
 * @property {number} totalLogIns - The number of times you logged in
 * @property {timeStamps} created - When the account was created
 * @property {number} ttl - The epoch time (seconds) when DynamoDB will delete this record 
 * (e.g. each log on sets it one year into the future for accounts, and temporary identities each new refresh token sets it two days in the future)
 * @property {timeStamps} lastLogIn - Last time the account was logged into
 * @property {number} streakMatchDaily - The number of days in a row with at least one game played
 * @property {number} streakMatchWeekly - The number of weeks in a row with at least one game played
 * @property {string} emailPrefs - if the value is found in this string, they that means they want that kind of email.
 * "daily", "weMissYou",  format is # separator, as in "daily#weMissYou"
 * @property {string} rank a string with three parts that looks like this “0348#2026-02-06T21:30:00#TJN5HWT” 
 * @property {string} password - password hash
 * @property {string} salt - password hash salt
 * @property {number} passwordResets - the number of times they did the forgot password thing
 * @property {number} passwordChanges - the number of times they changed their password 
 * @property {string} role - so far I got "admin" and "player". Not to be confused with match roles (owner, opponent, watcher)
 * @property {string} secQ - the users security question
 * @property {string} secA - the users security answer. Should probably hash this.
 * @property {string} scheme - the name of the preferred color scheme
 * @property {string} animationMode - the name of the preferred animation mode 
 * @property {boolean} amIRil  - are they ril?
 * @property {number} lastEmailSent - unix epoc time (seconds) of the last email that was sent
 * @property {[string]} badges - an array of badgeID's earned, e.g. "daysLoggedIn10", "daysLoggedIn20", etc.
 * /

// timeStamps type
/**
 * @typedef {object} timeStamps - Contains timeStamps in easily retreivable formats
 * @property {number} epoch - Number of seconds since 1/1/1970
 * @property {number} year - the year in four digits: YYYY
 * @property {number} month - the month in two digits: MM
 * @property {number} day - the day in two digits: DD
 */

/**
 * @typedef {object} refreshToken
 * @property {string} signature
 * @property {userID} userID
 * @property {number} exp
 */

/**
 * @typedef {object} identityToken
 * @property {identity} identity
 * @property {string} signature
 */

/**
 * @typedef {string}  userID
 */

const isUserIDValid = (userID) => {
  if (userID === null) {
    console.log("ERROR: The userID is null.");
    return false;
  }
  if (typeof userID != "string") {
    console.log("ERROR: The userID is not a string.");
    return false;
  }
  if (userID === "waiting") {
    return true;
  }
  if (userID === COMPUTER_USERID) {
    return true;
  }
  if (
    !(
      userID.length === 7 ||
      userID.length === 12 ||
      userID.length === 15 ||
      userID.length === 18 ||
      userID.length === 19
    )
  ) {
    console.log(
      "ERROR: The userID is not 7 or 12 or 15 characters long: " + userID,
    );
    console.trace();
    return false;
  }
  if (utilsApp.textGotForbiddenChars(userID)) {
    console.log("ERROR: The userID contains forbidden characters");
    return false;
  }
  if (userID.includes("O")) {
    console.log("ERROR: The userID has a forbbiden O."); // only temp ID's use capitals. it's okay for an account to have a "o"
    return false;
  }
  if (userID.includes("0")) {
    console.log("ERROR: The userID has a forbbiden 0.");
    return false;
  }
  return true;
};

const isSecQAValid = (secQA) => {
  const result = {
    status: -1,
    message: "",
  };
  if (secQA === null) {
    result.status = 400;
    result.message = "Can not be null";
    return result;
  }
  if (typeof secQA != "string") {
    result.status = 400;
    result.message = "Must be a string";
    return result;
  }
  if (secQA.length > SECQA_MAX_LENGTH) {
    result.status = 400;
    result.message = "Must be shorter than: " + SECQA_MAX_LENGTH;
    return result;
  }
  if (secQA.length < SECQA_MIN_LENGTH) {
    result.status = 400;
    result.message = "Must be at least four characters";
    return result;
  }
  //const forbiddenChars = [">", "<", "{", "}", "[", "]", ";", "*", "^", "%", "./","../"];

  for (const char of FORBIDDEN_CHARS) {
    if (secQA.includes(char)) {
      result.status = 400;
      result.message = "Cannot include forbidden character: " + char;
      return result;
    }
  }
  // Free text, so punctuation is fine, but no line breaks or other control
  // characters. (It's always escaped when shown.)
  if (hasControlChars(secQA)) {
    result.status = 400;
    result.message = "Cannot include line breaks or control characters";
    return result;
  }

  // if they made it through all that, then:
  result.status = 200;
  return result;
};

const isSearchValid = (name) => {
  const result = {
    status: -1,
    message: "",
  };
  if (name === null) {
    result.status = 400;
    result.message = "Can not be null";
    return result;
  }
  if (typeof name != "string") {
    result.status = 400;
    result.message = "Must be a string";
    return result;
  }
  if (name.length > NAME_MAX_LENGTH) {
    result.status = 400;
    result.message = "Must be shorter than: " + NAME_MAX_LENGTH;
    return result;
  }
  if (name.length < 2) {
    result.status = 400;
    result.message = "Must be at least two characters";
    return result;
  }

  //const forbiddenChars = [">", "<", "{", "}", "[", "]", ";", "*", "^", "%","./","../"];
  for (const char of FORBIDDEN_CHARS) {
    if (name.includes(char)) {
      result.status = 400;
      result.message = "Cannot include forbidden character: " + char;
      return result;
    }
  }
  // Shared rule: the characters names can have (textFilter_v2.js)
  if (!isSearchWellFormed(name)) {
    result.status = 400;
    result.message =
      "Use letters, numbers, apostrophes, periods, hyphens, and single spaces";
    return result;
  }

  // Shared filter: banned words and phrases (same rules as the back end)
  if (findBannedWords(name).length > 0) {
    result.status = 400;
    result.message = "Really?";
    return result;
  }

  // if they made it through all that, then:
  result.status = 200;
  return result;
};

/**
 * Tells you if a NAME PART is valid, for entering a new name: "Jay Garing",
 * not "Jay Garing (LIK3GSQ)". The server adds " (USERID)" and enforces the
 * same rules. To check a full stored name, use isFullNameShapeValid.
 * @param {name} string
 * @return {result} {result} i.e. an object with two keys:
 * * status: 200, 400, etc (standard http status codes)
 * * message: populated when validation fails, suitable for front end
 */
const isNameValid = (name) => {
  const result = {
    status: -1,
    message: "",
  };
  if (name === null) {
    result.status = 400;
    result.message = "Can not be null";
    return result;
  }
  if (typeof name != "string") {
    result.status = 400;
    result.message = "Must be a string";
    return result;
  }
  if (name.length > NAME_MAX_LENGTH) {
    result.status = 400;
    result.message = "Must be shorter than: " + NAME_MAX_LENGTH;
    return result;
  }
  if (name.length < 2) {
    result.status = 400;
    result.message = "Must be at least two characters";
    return result;
  }
  // first char has to be a valid write shard character,
  // but we can normalize it in case of diacritic marks
  if (
    !WRITE_SHARDS.includes(
      utilsApp.removeDiacritics(name[0].toLocaleLowerCase()),
    )
  ) {
    result.status = 400;
    result.message = "First character has to be a letter";
    return result;
  }

  //const forbiddenChars = [">", "<", "{", "}", "[", "]", ";", "*", "^", "%","./","../"];
  for (const char of FORBIDDEN_CHARS) {
    if (name.includes(char)) {
      result.status = 400;
      result.message = "Cannot include forbidden character: " + char;
      return result;
    }
  }
  // Shared rule (textFilter_v2.js, also enforced by the server): letters,
  // digits, apostrophes, periods, hyphens, single spaces. No parentheses.
  if (!isNamePartWellFormed(name)) {
    result.status = 400;
    result.message =
      "Use letters, numbers, apostrophes, periods, hyphens, and single spaces";
    return result;
  }

  // Shared filter: banned words and phrases (same rules as the back end)
  if (findBannedWords(name).length > 0) {
    result.status = 400;
    result.message = "Really?";
    return result;
  }

  // if they made it through all that, then:
  result.status = 200;
  return result;
};

/**
 * Tells you if a stored full name has the expected SHAPE: "namePart (USERID)"
 * with a valid userID, or "waiting". Doesn't apply the stricter rules for
 * entering a new name, so older names still load. Everything shown is
 * escaped, so this is about well-formed data, not safety.
 * @param {*} name
 * @returns {boolean}
 */
const isFullNameShapeValid = (name) => {
  if (name === "waiting") return true;
  if (typeof name !== "string") return false;
  const userID = parseUserID(name);
  if (userID.startsWith("ERROR")) return false;
  if (!name.endsWith(`(${userID})`)) return false;
  const namePart = parseName(name);
  return namePart.length > 0 && isUserIDValid(userID);
};

/**
 * Tells you if a blurb is valid.
 * @param {name} string
 * @return {result} {result} i.e. an object with two keys:
 * * status: 200, 400, etc (standard http status codes)
 * * message: populated when validation fails, suitable for front end
 */
const isBlurbValid = (blurb) => {
  const result = {
    status: -1,
    message: "",
  };
  if (blurb === null) {
    result.status = 400;
    result.message = "Can not be null";
    return result;
  }
  if (typeof blurb != "string") {
    result.status = 400;
    result.message = "Must be a string";
    return result;
  }
  if (blurb.length > BLURB_MAX_LENGTH) {
    result.status = 400;
    result.message = "Must be shorter than: " + BLURB_MAX_LENGTH;
    return result;
  }
  if (blurb.length < 2) {
    result.status = 400;
    result.message = "Must be at least two characters";
    return result;
  }
  for (const char of FORBIDDEN_CHARS) {
    if (blurb.includes(char)) {
      result.status = 400;
      result.message = "Cannot include forbidden character: " + char;
      return result;
    }
  }
  // Free text, so punctuation is fine, but no line breaks or other control
  // characters. (It's always escaped when shown.)
  if (hasControlChars(blurb)) {
    result.status = 400;
    result.message = "Cannot include line breaks or control characters";
    return result;
  }
  // Shared filter: banned words and phrases (same rules as the back end)
  if (findBannedWords(blurb).length > 0) {
    result.status = 400;
    result.message = "Really?";
    return result;
  }

  // if they made it through all that, then:
  result.status = 200;
  return result;
};

/**
 * Tells you if an email is valid
 * @param {email} string
 * @return {result} {result} i.e. an object with two keys:
 * * status: 200, 400, etc (standard http status codes)
 * * message: populated when validation fails, suitable for front end
 */
const isEmailValid = (email) => {
  const result = {
    status: -1,
    message: "",
  };
  if (email === null) {
    result.status = 400;
    result.message = "Can not be null";
    return result;
  }
  if (typeof email != "string") {
    result.status = 400;
    result.message = "Must be a string";
    return result;
  }
  if (email.length > EMAIL_MAX_LENGTH) {
    result.status = 400;
    result.message = "Can not be longer than: " + EMAIL_MAX_LENGTH;
    return result;
  }
  if (email.length < 6) {
    result.status = 400;
    result.message = "Must be at least six characters";
    return result;
  }
  if (!email.includes("@")) {
    result.status = 400;
    result.message = "Must have an @ ";
    return result;
  }
  if (email.length - email.replaceAll("@", "").length > 1) {
    result.status = 400;
    result.message = "Can not have more than one @ ";
    return result;
  }
  if (!email.includes(".")) {
    result.status = 400;
    result.message = "Must contain a .";
    return result;
  }
  const allowed = /^[a-zA-Z0-9\-_.@]+$/;
  if (!allowed.test(email)) {
    result.status = 400;
    result.message = "Can not have forbidden characters";
    return result;
  }

  // if they made it through all that, then:
  result.status = 200;
  return result;
};

/**
 * tells you if an email is already in use by another account
 * @param {email:string} email
 * @returns {{status:number, message: string}}
 ** status: 200 means email si available
 ** status: 409 means email is already in use
 ** status: 500 means something went wrong, check the logs on the lambda
 */
const isEmailTaken = async (email) => {
  const pAIdentity = (await getPlayingAs()).identity;
  if (isEmailValid(email).status != 200) {
    return { status: 400, message: "ERROR: The email format is not valid" };
  }
  let response = {};
  try {
    const resource = "/v1/identity";
    const body = JSON.stringify({
      idempotencyKey: idem.generateKey("identity"),
      action: "isEmailTaken",
      email,
    });
    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
      body: body,
    });
    if (!response.ok) {
      if (response.status === 409) {
        return {
          status: 409,
          message: "That email is already in use",
        };
      }
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    console.error(error.message);
    return {
      status: 500,
      message: "ERROR: unable to check if email is taken: " + error.message,
    };
  }

  return {
    status: 200,
    message: "The email is available.",
  };
};

/**
 * Tells you if an password is valid
 * @param {password} string
 * @return {result} {result} i.e. an object with two keys:
 * * status: 200, 400, etc (standard http status codes)
 * * message: populated when validation fails, suitable for front end
 */
const isPasswordValid = (password) => {
  const result = {
    status: -1,
    message: "",
  };
  if (password === null) {
    result.status = 400;
    result.message = "The password can not be null";
    return result;
  }
  if (typeof password != "string") {
    result.status = 400;
    result.message = "The password has to be a string";
    return result;
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    result.status = 400;
    result.message = "Can't be longer than: " + PASSWORD_MAX_LENGTH;
    return result;
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    result.status = 400;
    result.message = "Must be at least " + PASSWORD_MIN_LENGTH + " characters";
    return result;
  }
  if (BANNED_PASSWORDS.includes(password)) {
    result.status = 400;
    result.message = "Really?";
    return result;
  }

  // if they made it through all that, then:
  result.status = 200;
  return result;
};

const isRoleValid = (role) => {
  if (typeof role != "string") {
    return false;
  }
  if (IDENTITY_ROLES.includes(role)) {
    return true;
  } else {
    return false;
  }
};

const isReservedName = (name) => {
  if (typeof name != "string") {
    return false;
  }
  if (RESERVED_NAMES.includes(name)) {
    return true;
  } else {
    return false;
  }
};

const isTypeValid = (type) => {
  if (typeof type != "string") {
    return false;
  }
  if (IDENTITY_TYPES.includes(type)) {
    return true;
  } else {
    return false;
  }
};

const isWriteShardValid = (writeShard) => {
  if (typeof writeShard != "string") {
    return false;
  }
  if (WRITE_SHARDS.includes(writeShard)) {
    return true;
  } else {
    return false;
  }
};

const isEmailPreferencesValid = (prefs) => {
  if (typeof prefs != "object") {
    return false;
  }
  const keys = Object.keys(prefs);
  if (!keys.includes("account")) {
    return false;
  }
  if (!keys.includes("match")) {
    return false;
  }
  if (!keys.includes("marketing")) {
    return false;
  }
  if (typeof prefs.account != "boolean") {
    return false;
  }
  if (typeof prefs.match != "boolean") {
    return false;
  }
  if (typeof prefs.marketing != "boolean") {
    return false;
  }
  return true;
};

// there is no userID at this point, because that is
// created on the back end
const isIdentityValid = (identity) => {
  if (!(typeof identity === "object")) {
    const message = "ERROR: The identity is not an object.";
    return { status: 400, message };
  }
  if (identity === null) {
    const message = "ERROR: The identity is null.";
    return { status: 400, message };
  }
  if (Array.isArray(identity)) {
    const message = "ERROR: The identity is an array.";
    return { status: 400, message };
  }
  const keys = Object.keys(identity);
  if (!keys.includes("name")) {
    const message = "ERROR: The identity does not have a name.";
    return { status: 400, message };
  }
  const nameResult = isNameValid(identity.name);
  if (nameResult.status != 200) {
    const message =
      "ERROR: The name is not valid: " +
      identity.name +
      " Message: " +
      nameResult.message;
    return { status: 400, message };
  }
  if (!keys.includes("allItems")) {
    const message = "ERROR: The identity does not have key called 'allItems'";
    return { status: 400, message };
  }
  if (identity.allItems != "allItems") {
    const message =
      "ERROR: The value 'allItems' can only be 'allItems'. Instead, it is: " +
      identity.allItems;
    return { status: 400, message };
  }
  // temp identities don't have email, password, secA, secQ, or amRil
  if (keys.includes("email")) {
    const emailResult = isEmailValid(identity.email);
    if (emailResult.status != 200) {
      const message =
        "ERROR: The email is not valid: " +
        identity.email +
        " Message: " +
        emailResult.message;
      return { status: 400, message };
    }
  }
  if (keys.includes("password")) {
    const passwordResult = isPasswordValid(identity.password);
    if (passwordResult.status != 200) {
      const message =
        "ERROR: The password is not valid. Message: " + result.message;
      return { status: 400, message };
    }
  }
  if (keys.includes("passwordResets")) {
    if (typeof identity.passwordResets != "number") {
      const message =
        "ERROR: The typeof passwordResets is not valid: " +
        typeof identity.passwordResets;
      return { status: 400, message };
    }
  }

  if (keys.includes("secQ")) {
    const secQResult = isSecQAValid(identity.secQ);
    if (secQResult.status != 200) {
      const message =
        "ERROR: The security question is not valid: " +
        identity.secQ +
        " Message: " +
        secQResult.message;
      return { status: 400, message };
    }
  }
  if (keys.includes("secA")) {
    const secAResult = isSecQAValid(identity.secA);
    if (secAResult.status != 200) {
      const message =
        "ERROR: The security answer is not valid: " +
        identity.secA +
        " Message: " +
        secAResult.message;
      return { status: 400, message };
    }
  }

  if (!keys.includes("created")) {
    const message = "ERROR: The identity does not have key called 'created'";
    return { status: 400, message };
  }
  if (typeof identity.created != "object") {
    const message = "ERROR: The created timeStamps are not an object";
    return { status: 400, message };
  }
  const createdResult = dateTimeUtils.areTimeStampsValid(identity.created);
  if (createdResult.status != 200) {
    const message =
      "ERROR: The created timeStamps are not valid: " + createdResult.message;
    return { status: 400, message };
  }

  if (!keys.includes("lastLogIn")) {
    const message = "ERROR: The identity does not have key called 'lastLogIn'";
    return { status: 400, message };
  }
  if (typeof identity.lastLogIn != "object") {
    const message = "ERROR: The lastLogIn timeStamps are not an object";
    return { status: 400, message };
  }
  const lastLogInResult = dateTimeUtils.areTimeStampsValid(identity.lastLogIn);
  if (createdResult.status != 200) {
    const message =
      "ERROR: The lastLogIn timeStamps are not valid: " +
      lastLogInResult.message;
    return { status: 400, message };
  }

  if (!keys.includes("totalWins")) {
    const message = "ERROR: The identity is missing totalWins";
    return { status: 400, message };
  }
  if (typeof identity.totalWins != "number") {
    const message =
      "ERROR: The totalWins is supposed to be a number, but instead its " +
      typeof identity.totalWins;
    return { status: 400, message };
  }
  if (typeof identity.totalLogIns != "number") {
    const message =
      "ERROR: The totalLogIns is supposed to be a number, but instead its " +
      typeof identity.totalLogIns;
    return { status: 400, message };
  }
  if (!keys.includes("totalLogIns")) {
    const message = "ERROR: The identity is missing totalLogIns";
    return { status: 400, message };
  }
  if (!keys.includes("totalMatches")) {
    const message = "ERROR: The identity is missing totalMatches";
    return { status: 400, message };
  }
  if (typeof identity.totalMatches != "number") {
    const message =
      "ERROR: The totalMatches is supposed to be a number, but instead its " +
      typeof identity.totalMatches;
    return { status: 400, message };
  }
  if (!keys.includes("streakMatchDaily")) {
    const message = "ERROR: The identity is missing streakMatchDaily";
    return { status: 400, message };
  }
  if (typeof identity.streakMatchDaily != "number") {
    const message =
      "ERROR: The streakMatchDaily is supposed to be a number, but instead its " +
      typeof identity.streakMatchDaily;
    return { status: 400, message };
  }
  if (!keys.includes("streakMatchWeekly")) {
    const message = "ERROR: The identity is missing streakMatchWeekly";
    return { status: 400, message };
  }
  if (typeof identity.streakMatchWeekly != "number") {
    const message =
      "ERROR: The streakMatchWeekly is supposed to be a number, but instead its " +
      typeof identity.streakMatchWeekly;
    return { status: 400, message };
  }
  if (!keys.includes("role")) {
    const message = "ERROR: The identity does not include a role";
    return { status: 400, message };
  }
  if (!isRoleValid(identity.role)) {
    const message = "ERROR: The roles is not valid: " + identity.role;
    return { status: 400, message };
  }
  if (!keys.includes("type")) {
    const message = "ERROR: The identity does not include the type key";
    return { status: 400, message };
  }
  if (!isTypeValid(identity.type)) {
    const message = "ERROR: The value for type is not valid: " + identity.type;
    return { status: 400, message };
  }
  if (!keys.includes("ttl")) {
    const message = "ERROR: The ttl key is missing";
    return { status: 400, message };
  }
  if (typeof identity.ttl != "number") {
    const message =
      "ERROR: The ttl is supposed to be a number, but instead its " +
      typeof identity.ttl;
    return { status: 400, message };
  }
  if (!dateTimeUtils.isEpochValid(identity.ttl)) {
    const message = "ERROR: ttl is not a valid epoch time: " + identity.ttl;
    return { status: 400, message };
  }
  if (!keys.includes("writeShard")) {
    const message = "ERROR: The writeShard key is missing";
    return { status: 400, message };
  }
  if (!isWriteShardValid(identity.writeShard)) {
    const message =
      "ERROR: The writeShard is not valid: " + identity.writeShard;
    return { status: 400, message };
  }
  if (!keys.includes("badges")) {
    const message = "ERROR: The badges key is missing";
    return { status: 400, message };
  }
  if (!Array.isArray(identity.badges)) {
    const message = "ERROR: Badges is not an array";
    return { status: 400, message };
  }
  if (!keys.includes("scheme")) {
    const message = "ERROR: The scheme key is missing";
    return { status: 400, message };
  }
  if (!colors.isSchemeValid(identity.scheme)) {
    const message =
      "ERROR: The value of scheme is not valid: " + identity.scheme;
    return { status: 400, message };
  }
  if (!keys.includes("animationMode")) {
    const message = "ERROR: The animationMode key is missing";
    return { status: 400, message };
  }
  if (!isAnimationModeValid(identity.animationMode)) {
    const message =
      "ERROR: The value of animationMode is not valid: " +
      identity.animationMode;
    return { status: 400, message };
  }
  return { status: 200, message: "" };
};

/**
 * Creates an identity. Does NOT set it as PlayingAs.
 * @param {{identity}} object the input object keys are as follows:
 * * name is required. Literally their name only without the id in parents.
 * * password is optional. If omitted, it means this a temporary identity that doesn't have a password.
 * * email is optional. If omited, it means this is a temporary identity that doesn't have email.
 * * amIRil is optional. If omitted, it means this is a temporary identity that is definitely not ril.
 * * secQ is optional. Only accounts have these, but it's optional for them. They don't have to have a security Question.
 * * secA is optional. Only accounts have these, but it's optional for them. They don't have to have a security Answer.
 * @returns {{status: number, identity: identity, message: string}} The return object has these keys:
 * * status could be 200 or 500 or 400 0r 409 (email already taken)
 * * identity: if the status is 200, then this will contain the newly created identity
 * * message:
 *
 */
const createIdentity = async (identity) => {
  let response;
  if (identity.type === "temp") {
    try {
      const resource = "/v1/temp-identity";
      const body = JSON.stringify({
        idempotencyKey: idem.generateKey("identity"),
      });
      response = await fetch(app.getAPI_URL() + resource, {
        method: "POST",
        body: body,
      });
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }
    } catch (error) {
      console.error(error.message);
      return { status: 500, message: error.message, identity: {} };
    }
  }

  if (identity.type === "account") {
    if (isReservedName(identity.name)) {
      return {
        status: 400,
        message: "This name is reseved by the system. Choose a different name.",
        identity: {},
      };
    }
    const isNameValidResult = isNameValid(identity.name);
    if (isNameValidResult.status != 200) {
      return {
        status: 400,
        message: "Name error: " + isNameValidResult.message,
        identity: {},
      };
    }
    const takenResult = await isEmailTaken(identity.email);
    switch (takenResult.status) {
      case 500:
        return {
          status: 500,
          message: "ERROR: Unable to check if email is taken ",
          identity: {},
        };
        break;
      case 409:
        return {
          status: 409,
          message: "The email is already in use",
          identity: {},
        };
        break;
      case 200:
      // email is valid
    }

    const pAIdentity = (await getPlayingAs()).identity;
    if (pAIdentity.identityToken.identity.exp <= dateTimeUtils.epochTime()) {
      // identity token has expired, so
      const refreshResult = await getNewIdentityToken(identity.refreshToken);
      if (refreshResult.status != 200) {
        return { status: 401, message: "", identity: {} };
      }
    }
    try {
      const resource = "/v1/identity";
      const body = JSON.stringify({
        idempotencyKey: idem.generateKey("identity"),
        action: "createAccount",
        identity,
      });
      response = await fetch(app.getAPI_URL() + resource, {
        method: "POST",
        headers: {
          authorization:
            "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
        },
        body: body,
      });
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }
    } catch (error) {
      console.error(error.message);
      return { status: 500, message: error.message, identity: {} };
    }
  }

  const bodyAsText = await response.text();
  const bodyAsJSON = JSON.parse(bodyAsText);
  const identityToken = bodyAsJSON.identityToken;
  const refreshToken = bodyAsJSON.refreshToken;
  const createdIdentity = identityToken.identity;

  createdIdentity.identityToken = JSON.parse(JSON.stringify(identityToken)); // got to keep track of original token from back end
  createdIdentity.refreshToken = JSON.parse(JSON.stringify(refreshToken));

  identityState.cache.push(createdIdentity);

  return {
    status: 200,
    message: "",
    identity: createdIdentity,
  };
};

const loadIdentity = async (userID) => {
  const pAIdentity = (await getPlayingAs()).identity;
  let response;
  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      pAUserID: pAIdentity.userID,
      userID2get: userID,
      action: "getIdentity",
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    let status2Return = 500; //default
    if (Object.keys(error).includes("status")) {
      status2Return = error.status;
    }
    if (Object.keys(error).includes("statusCode")) {
      status2Return = error.statusCode;
    }

    return {
      status: status2Return,
      message: "Error: Unable to get the identity",
    };
  }

  const bodyAsText = await response.text();
  const bodyAsJSON = JSON.parse(bodyAsText);
  const identityToken = bodyAsJSON.identityToken;
  const refreshToken = bodyAsJSON.refreshToken;
  await identityService.updatePlayingAsTokens({ identityToken, refreshToken });

  const identitySearchedFor = bodyAsJSON.identitySearchedFor;
  addIdentities([identitySearchedFor]); // add to local cache
  return { status: 200, identity: identitySearchedFor };
};

/**
 * Tells you if the playingAs identity is temporary or an account
 * @returns boolean
 */
const gotLoggedOnUser = () => {
  const userID = parseUserID(identityState.playingAsName);
  return !userID.startsWith("Temp_"); //true or false
};

/**
 * returns the identity that matches the userID.
 * If found, it will be in the "identity" key of the return object
 * IF not, the status will come back as 404
 * @returns {{status: number, identity: identity}}
 */
const getIdentity = async (userID) => {
  // Debug logging to track ALL getIdentity calls
  console.log("getIdentity() called with userID:", userID, "type:", typeof userID);
  
  // Debug logging to track invalid userID calls
  if (!isUserIDValid(userID)) {
    const message = "ERROR: userID failed validation check: " + userID;
    console.log(message);
    console.log("getIdentity() call stack:", new Error().stack);
    return { status: 400, error: message, identity: {} };
  }

  for (const identity of identityState.cache) {
    if (identity.userID === userID) {
      // not all identities have tokens
      // example: search results
      // only identities created in this session or logged into have tokens
      if (Object.keys(identity).includes("refreshToken")) {
        const tokenResponse = await dealWithTokens({
          identityToken: identity.identityToken,
          refreshToken: identity.refreshToken,
        });
        if (tokenResponse.status != 200) {
          // session expired
          return { status: tokenResponse.status, identity: {} };
        }
      }
      return { status: 200, identity };
    }
  }

  const pAIdentity = (await getPlayingAs()).identity;
  let response;
  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      pAUserID: pAIdentity.userID,
      userID2get: userID,
      action: "getIdentity",
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    //console.log("Error: Unable to getIdentity: " + userID);
    //console.error(error.message);
    let status2Return = 500; //default
    if (Object.keys(error).includes("status")) {
      status2Return = error.status;
    }
    if (Object.keys(error).includes("statusCode")) {
      status2Return = error.statusCode;
    }
    // 404 means the identity no longer exists, therefore
    // we gotta set the match they are in to abandoned
    const currentMatchResult = matchService.getCurrentMatch();
    if (currentMatchResult.status != 200) {
      // There is no match, so there's nothing to abandon
      // so get out of here
      return {
        status: status2Return,
        message: "Error: Unable to get the identity",
      };
    }
    const currentMatch = currentMatchResult.currentMatch;
    // make sure this identity is actually in the current match
    if (currentMatch.p1UserID === userID || currentMatch.p2UserID === userID) {
      const setMatchResult = matchService.setMatchState("abandoned");
      if (setMatchResult != 200) {
        return {
          status: status2Return,
          message: `Error: Unable to get the identity ${userID} and unable to set match ${currentMatch.matchID} to abandoned`,
        };
      }
      // gone
      return {
        status: 410,
        message: `Unable to get the identity ${userID}, who was a player in ${currentMatch.matchID}. Therefore, successfully set it to abandoned`,
      };
    }

    return {
      status: status2Return,
      message: "Error: Unable to get the identity",
    };
  }

  const bodyAsText = await response.text();
  const bodyAsJSON = JSON.parse(bodyAsText);
  const identityToken = bodyAsJSON.identityToken;
  const refreshToken = bodyAsJSON.refreshToken;
  await identityService.updatePlayingAsTokens({ identityToken, refreshToken });

  const identitySearchedFor = bodyAsJSON.identitySearchedFor;
  addIdentities([identitySearchedFor]); // add to local cache
  return { status: 200, identity: identitySearchedFor };
};

/**
 * Set the email of the playing as identity, but only if it's an account
 * @param {string} email
 * @returns {status:number, message:string}
 */

const setEmail = async (email, currentPassword) => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status, message: "Your session expired" };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;

  if (pAIdentity.type != "account") {
    const message =
      "ERROR: You can only change the email of an account, but you are playing as a temp identity " +
      pAIdentity.name;
    console.log(message);
    return { status: 400, error: message, message };
  }
  const result = isEmailValid(email);
  if (result.status != 200) {
    const message = "The new email is not valid: " + result.message;
    return { status: 400, error: message, message };
  }

  const takenResult = await isEmailTaken(email);
  switch (takenResult.status) {
    case 409: {
      const message = "That email is already in use";
      return { status: 409, error: message, message };
    }
    case 200: {
      // The server checks the current password, format, and uniqueness too.
      const updateResult = await storeAttribute({
        userID: pAUserID,
        attribute: "email",
        newValue: email,
        currentPassword,
      });
      if (updateResult.status === 200) {
        return { status: 200, error: "", message: "" };
      }
      return {
        status: updateResult.status,
        error: updateResult.message,
        message: updateResult.message,
      };
    }
    default: {
      const message = "ERROR: Unable to set the email";
      return { status: 500, error: message, message };
    }
  }
};

/**
 * setName will change the name of any identity for which this session is
 * an authoritative (i.e. got refresh tokens because we made it or logged onto it),
 * which is why you have to pass in the userID. This is necessary for changing the names
 * of temp identities in a two player same device match
 * @param {{ userID: string, name:string, blurb, string }} param0
 * @returns {{status:string, message:string}}
 */
const setName = async ({ userID, name, blurb }) => {
  if (!isUserIDValid(userID)) {
    const message = "ERROR: userID failed validation check: " + userID;
    console.log(message);
    return { status: 400, error: message };
  }
  if (name.length === 0 && blurb.length === 0) {
    return { status: 400, error: "Enter a new name, a new blurb, or both" };
  }
  const resultNameValid = isNameValid(name);
  if (name.length > 0 && resultNameValid.status != 200) {
    return { status: 400, error: "Name: " + resultNameValid.message };
  }
  const resultBlurbValid = isBlurbValid(blurb);
  if (blurb.length > 0 && resultBlurbValid.status != 200) {
    return { status: 400, error: "Blurb: " + resultBlurbValid.message };
  }
  for (const identity of identityState.cache) {
    if (identity.userID === userID) {
      if (!Object.keys(identity).includes("refreshToken")) {
        return {
          status: 401,
          error:
            "Not authorized to change the name of this identity:" +
            identity.name,
        };
      }
      if (blurb.length > 0) {
        let params = {
          userID,
          attribute: "blurb",
          newValue: blurb,
          type: identity.type,
        };
        const resultStoreBlurb = await storeAttribute(params);
        if (name.length === 0) {
          if (resultStoreBlurb.status === 200) {
            return { status: 200, error: "" };
          } else {
            // (this returned the validation status, 200, so a failed save
            // looked like success)
            return {
              status: resultStoreBlurb.status,
              error: resultStoreBlurb.message || "Unable to save the blurb",
            };
          }
        }
      }
      if (name.length > 0) {
        const newName = name + " (" + userID + ")";
        let params = {
          userID,
          attribute: "name",
          newValue: newName,
          type: identity.type,
        };
        const resultNameStore = await storeAttribute(params);
        if (resultNameStore.status === 200) {
          matchService.updateUserName({ userID, name }); // updates any matches in cache
          await setPlayingAs({ name: newName, createMatch: false });
          return { status: 200, error: "" };
        } else {
          return {
            status: resultNameStore.status,
            error: resultNameStore.message || "Unable to save the name",
          };
        }
      } // end name >0
    } // end if userID=userIF
  } // end for
};

const getNamePart = async (userID) => {
  if (!isUserIDValid(userID)) {
    const message = "ERROR: userID failed validation check: " + userID;
    console.log(message);
    return { status: 400, error: message };
  }
  const result = await identityService.getIdentity(userID);
  if (result.status != 200) {
    const message = "ERROR: could not find identity for: " + userID;
    console.log(message);
    return { status: 404, error: message };
  }
  return {
    status: 200,
    namePart: identityService.parseName(result.identity.name),
  };
};
/**
 * Retuns only the userID without the name.
 * @param {"Jay Garing (DF4THCSS)"} name
 * * The input must be a string in this format: "Jay Garing (DF4THCSS)"
 * @returns {"DF4THCSS"}
 * * returns the userID only
 */
const parseUserID = (name) => {
  if (typeof name != "string") {
    return "ERROR: The name is not a string";
  }
  // if (name === COMPUTER_NAME) {
  //   return name;
  // }
  if (!name.includes("(")) {
    return "ERROR: Name did not contain an open parenthesis";
  }
  if (!name.includes(")")) {
    return "ERROR: Name did not contain a close parenthesis";
  }
  const openParenIndex = name.lastIndexOf("(");
  const closeParenIndex = name.lastIndexOf(")");
  const userID = name.slice(openParenIndex + 1, closeParenIndex);
  return userID;
};

/**
 * Retuns only the name without the userID.
 * @param {"Jay Garing (DF4THCSS)"} name
 * * The input must be a string in this format: "Jay Garing (DF4THCSS)"
 * @returns {"Jay Garing"}
 * * returns the name part only
 */
const parseName = (name) => {
  if (typeof name != "string") {
    return "ERROR: The name is not a string";
  }
  if (!name.includes("(")) {
    return "ERROR: Name did not contain an open parenthesis";
  }
  if (!name.includes(")")) {
    return "ERROR: Name did not contain a close parenthesis";
  }
  const openParenIndex = name.lastIndexOf("(");
  const namePart = name.slice(0, openParenIndex);
  return namePart.trim();
};

/**
 * @brief Returns the full identity object that we are playing as.
 * It handles checking for expired tokens, trying to renew them, and
 * logging out if it can't.
 * Status is 200 if successful or 400 if it's not been set yet
 * @returns {{status: number, identity: identity}}
 */
const getPlayingAs = async () => {
  if (identityState.playingAsName === "notSet") {
    const message = "ERROR: The playing as identity has not been set yet.";
    return { status: 400, error: message };
  }
  const userID = parseUserID(identityState.playingAsName);
  
  // Check if parseUserID returned an error
  if (typeof userID === "string" && userID.startsWith("ERROR:")) {
    console.log("getPlayingAs: parseUserID failed with:", userID);
    console.log("getPlayingAs: playingAsName was:", identityState.playingAsName);
    const message = `getPlayingAs failed: ${userID}`;
    return { status: 400, error: message };
  }
  
  const namePart = parseName(identityState.playingAsName);
  let pAIdentity = await getIdentity(userID);
  if (pAIdentity.status != 200) {
    return {
      // could be logged out due to expired tokens
      status: pAIdentity.status,
      identity: {},
    };
  }
  pAIdentity.identity.pANameHtml = generateNameHtml({ namePart, userID });
  if (Object.keys(pAIdentity.identity).includes("blurb")) {
    const blurb = pAIdentity.identity.blurb;
    pAIdentity.identity.pANameHtmlBlurb = generateNameHtmlBlurb({
      namePart,
      userID,
      blurb,
    });
  }
  return pAIdentity;
};

// SECURITY: these return HTML, so every user value in them is escaped.
const generateNameHtml = ({ namePart, userID }) => {
  return `${escapeHtml(namePart)} <span class='pAUserIDColor'>(${escapeHtml(userID)})</span>`;
};
const generateNameHtmlBlurb = ({ namePart, userID, blurb }) => {
  return (
    generateNameHtml({ namePart, userID }) + `<br><em>${escapeHtml(blurb)}</em>`
  );
};

/**
 * sets the playingAs identity.
 * @param {{name:string, createMatch:boolean}}
 * * name has to be in full format. e.g. Jay Garing (LPD8CVZ)
 * * createAMatch is true false. The purpose is to have the option to only create a match on page load.
 * When you log out, there still HAS to be an identity that is playingAs, but in this case, it's
 * confusing if a new match is created. so this is false in that case.
 * @returns {{status: number, error: message}}
 */

const setPlayingAs = async ({ name, createMatch }) => {
  const userID = parseUserID(name);
  if (!isUserIDValid(userID)) {
    const message = "ERROR: name failed userID validation check: " + name;
    console.log(message);
    return { status: 400, error: message };
  }
  // Shape only: an older name that predates the current naming rules must
  // still be playable.
  if (!isFullNameShapeValid(name)) {
    const message = "ERROR: failed name validation check: " + name;
    console.log(message);
    return { status: 400, error: message };
  }

  const result = await getIdentity(userID); // has to be in the cache
  if (result.status === 200) {
    if (!Object.keys(result.identity).includes("refreshToken")) {
      return {
        status: 401,
        error: "Not authorized to play as this identity:" + name,
      };
    }

    identityState.playingAsName = name; // and that's it

    // unsubscribe from any previous identities events
    await subscriptionManager.closeAllSubscriptions();
    // and subscribe to theirs
    await subscriptionManager.subscribeMatchParticipants({
      userID: result.identity.userID,
      tokenString:
        "Bearer " + utilsApp.base64UrlEncode(result.identity.identityToken),
    });

    await matchService.loadPlayingAsMatches({ createMatch }); // load their matches

    return { status: 200, error: "" };
  } else {
    const message = "ERROR: name could not be found: " + name;
    console.log(message);
    return { status: 404, error: message };
  }
};

const logOn = async ({ email, password }) => {
  const pAIdentityResponse = await getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status, identity: {} };
  }
  const pAIdentity = pAIdentityResponse.identity;

  let response;
  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      email: email,
      password: password,
      action: "logOn",
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    console.error(error.message);
  }

  const responseText = await response.text();
  let message = "";

  let responseBodyAsObj = {};
  try {
    responseBodyAsObj = JSON.parse(responseText);
  } catch (error) {
    console.log("Unable to JSON parse response");
    console.error(error.message);
    return {
      status: 500,
      message: "Unable to process log in request",
    };
  }

  if (response.status != 200) {
    // Too many wrong passwords: say so, instead of "not found"
    if (
      typeof responseBodyAsObj.message === "string" &&
      responseBodyAsObj.message.startsWith("Too many")
    ) {
      return { status: 429, message: responseBodyAsObj.message };
    }
    return {
      status: 404,
      message:
        "Could not find an account with that email / password combination",
    };
  }
  // log in was successful
  // is the identity already in the cache?
  // the code below cannot use updatePlayingAsTokens({identityToken, refreshToken}), because
  // it's a log on. The playing as identity has not been updated yet. The tokens have to be
  // in place first before we can update the logged on identity to the playing as identity. So it's a chicken and egg
  // problem, so that's why there are these two functions only used by logon
  // updateIdentityInCacheWToken and addIdentityToCacheFromToken
  let inCache = false;
  for (const identity of identityState.cache) {
    if (responseBodyAsObj.identityToken.identity.userID === identity.userID) {
      updateIdentityInCacheWToken({
        identity,
        identityToken: responseBodyAsObj.identityToken,
      });
      inCache = true;
      identity.refreshToken = responseBodyAsObj.refreshToken;
      break;
    }
  }
  // it wasn't in the cache, so let's add it
  if (inCache === false) {
    await addIdentityToCacheFromToken(responseBodyAsObj.identityToken);
    const newIdentity = (
      await getIdentity(responseBodyAsObj.identityToken.identity.userID)
    ).identity;
    newIdentity.refreshToken = responseBodyAsObj.refreshToken;
  }

  // now that the cache is correct:
  await identityService.setPlayingAs({
    name: responseBodyAsObj.identityToken.identity.name,
    createMatch: false,
  });

  return { status: 200, message: "" };
};

/**
 * Allows you to change the security question and answer for the logged on user, if
 * it's an actual account.
 * @param {{secQ:string, secA:string}}
 * @returns {{status:number, message:string}}
 */
const setSecQA = async ({ secQ, secA, currentPassword }) => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;

  if (pAIdentity.type != "account") {
    return {
      status: 400,
      message: `ERROR: You can only change security questions for an 
      account, but you are playing as a temporary identity`,
    };
  }
  if (!Object.keys(pAIdentity).includes("refreshToken")) {
    return {
      status: 401,
      message:
        "ERROR: Not authorized to make changes to this identity:" +
        pAIdentity.name,
    };
  }
  const secQResult = isSecQAValid(secQ);
  if (secQResult.status != 200) {
    return {
      status: 400,
      message: "ERROR: The security question is not valid",
    };
  }
  const secAResult = isSecQAValid(secA);
  if (secAResult.status != 200) {
    return { status: 400, message: "ERROR: The security answer is not valid" };
  }
  if (secQResult.status === 200 && secAResult.status === 200) {
    const params = {
      userID: pAIdentity.userID,
      attribute: "secQA",
      newValue: { secQ, secA },
      currentPassword,
    };
    const result = await storeAttribute(params);
    if (result.status === 200) {
      return { status: 200, error: "", message: "" };
    }
    return {
      status: result.status,
      error: result.message,
      message: result.message || "Error: Unable to update the security question",
    };
  }
};

/**
 * Lets you change the password for the playing as identity's
 * account when the old password is known
 * @param {*} param0
 * @returns {{status: number, message: string}}
 * * status is 200 or 400
 * * message is the explanation
 */
const passwordChange = async ({ oldPassword, newPassword }) => {
  if (
    isPasswordValid(oldPassword).status != 200 ||
    isPasswordValid(newPassword).status != 200
  ) {
    return {
      status: 400,
      message: "The new password is not valid",
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  // probably not necessary, but why not double check to be sure:
  if (pAIdentity.type != "account") {
    const message =
      "ERROR: You can only change the email of an account, but you are playing as a temp identity " +
      pAIdentity.name;
    return { status: 400, error: message };
  }

  const params = {
    userID: pAUserID,
    attribute: "password",
    newValue: { oldPassword, newPassword },
  };
  const result = await storeAttribute(params);
  if (result.status === 200) {
    return { status: 200, error: "" };
  }
  // The server explains: wrong current password, too many tries, or a new
  // password that breaks the rules.
  const message =
    result.status === 401 && !result.message
      ? "Error: Current password not correct"
      : "Error: " + (result.message || "Unable to change the password");
  return { status: result.status, error: message, message };
};

/**
 * @brief Sets the color scheme of the playing as identity
 * @param {scheme: string}
 * @return {{status: number, message: string}} The return object is:
 * * status is 200 or 400
 * * message is an error message if the status is 400
 */
const setScheme = async (scheme) => {
  if (!colors.isSchemeValid(scheme)) {
    return { status: 400, message: "This scheme is not valid: " + scheme };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  const params = {
    userID: pAUserID,
    attribute: "scheme",
    newValue: scheme,
    type: pAIdentity.type,
  };
  const updateResult = await storeAttribute(params);
  if (updateResult.status === 200) {
    return { status: 200, error: "" };
  }
  if (updateResult.status === 401) {
    return { status: 401, error: "Error: Not authorized" };
  }
  if (updateResult.status != 200) {
    return { status: 500, error: "Error: API unable to update color scheme" };
  }
};

/**
 * @brief Sets the animation mode of the playing as identity
 * @param {animationMode: string}
 * @return {{status: number, message: string}} The return object is:
 * * status is 200 or 400
 * * message is an error message if the status is 400
 */
const setAnimationMode = async (animationMode) => {
  if (!isAnimationModeValid(animationMode)) {
    return {
      status: 400,
      message: "This animationMode is not valid: " + animationMode,
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  const params = {
    userID: pAUserID,
    attribute: "animationMode",
    newValue: animationMode,
    type: pAIdentity.type,
  };
  const updateResult = await storeAttribute(params);
  if (updateResult.status === 200) {
    return { status: 200, error: "" };
  }
  if (updateResult.status === 401) {
    return { status: 401, error: "Error: Not authorized" };
  }
  if (updateResult.status != 200) {
    return { status: 500, error: "Error: API unable to update animationMode" };
  }
};

const setChangedPop = async (setting) => {
  if (typeof setting != "boolean") {
    return {
      status: 400,
      error: "Error: You can only set this to true or false",
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  const params = {
    userID: pAUserID,
    attribute: "changedPop",
    newValue: setting,
  };
  const updateResult = await storeAttribute(params);
  if (updateResult.status === 200) {
    return { status: 200, error: "" };
  }
  if (updateResult.status === 401) {
    return { status: 401, error: "Error: Not authorized" };
  }
  if (updateResult.status != 200) {
    return { status: 500, error: "Error: API unable to update postLogPop" };
  }
};

const setCreatedPop = async (setting) => {
  if (typeof setting != "boolean") {
    return {
      status: 400,
      error: "Error: You can only set this to true or false",
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  const params = {
    userID: pAUserID,
    attribute: "createdPop",
    newValue: setting,
  };
  const updateResult = await storeAttribute(params);
  if (updateResult.status === 200) {
    return { status: 200, error: "" };
  }
  if (updateResult.status === 401) {
    return { status: 401, error: "Error: Not authorized" };
  }
  if (updateResult.status != 200) {
    return { status: 500, error: "Error: API unable to update postLogPop" };
  }
};

const setJoinedPlayerPop = async (setting) => {
  if (typeof setting != "boolean") {
    return {
      status: 400,
      error: "Error: You can only set this to true or false",
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  const params = {
    userID: pAUserID,
    attribute: "joinedPlayerPop",
    newValue: setting,
  };
  const updateResult = await storeAttribute(params);
  if (updateResult.status === 200) {
    return { status: 200, error: "" };
  }
  if (updateResult.status === 401) {
    return { status: 401, error: "Error: Not authorized" };
  }
  if (updateResult.status != 200) {
    return { status: 500, error: "Error: API unable to update postLogPop" };
  }
};

const setJoinedWatcherPop = async (setting) => {
  if (typeof setting != "boolean") {
    return {
      status: 400,
      error: "Error: You can only set this to true or false",
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  const params = {
    userID: pAUserID,
    attribute: "joinedWatcherPop",
    newValue: setting,
  };
  const updateResult = await storeAttribute(params);
  if (updateResult.status === 200) {
    return { status: 200, error: "" };
  }
  if (updateResult.status === 401) {
    return { status: 401, error: "Error: Not authorized" };
  }
  if (updateResult.status != 200) {
    return { status: 500, error: "Error: API unable to update postLogPop" };
  }
};

const setPostLogPop = async (setting) => {
  if (typeof setting != "boolean") {
    return {
      status: 400,
      error: "Error: You can only set this to true or false",
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  const params = {
    userID: pAUserID,
    attribute: "postLogPop",
    newValue: setting,
  };
  const updateResult = await storeAttribute(params);
  if (updateResult.status === 200) {
    return { status: 200, error: "" };
  }
  if (updateResult.status === 401) {
    return { status: 401, error: "Error: Not authorized" };
  }
  if (updateResult.status != 200) {
    return { status: 500, error: "Error: API unable to update postLogPop" };
  }
};

const setEmailPrefs = async (prefs) => {
  if (!isEmailPreferencesValid(prefs)) {
    return {
      status: 400,
      message: "The email prefs are not valid: " + JSON.stringify(prefs),
    };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  const params = {
    userID: pAUserID,
    attribute: "emailPrefs",
    newValue: prefs,
  };
  const updateResult = await storeAttribute(params);
  if (updateResult.status === 200) {
    return { status: 200, error: "" };
  }
  if (updateResult.status === 401) {
    return { status: 401, error: "Error: Not authorized" };
  }
  if (updateResult.status != 200) {
    return { status: 500, error: "Error: API unable to update email prefs" };
  }
};

/**
 * @brief returns the animation mode of the playing as identity
 * @return {string} the string is the animation mode
 * * "serial", "parallel", or "off"
 */
const getAnimationMode = () => {
  for (const identity of identityState.cache) {
    if (identity.name === identityState.playingAsName) {
      return identity.animationMode;
    }
  }
};

/**
 * Gets the playing as account's private details from the server: email,
 * emailVerified, secQ, hasSecQA. The identity token doesn't carry email or
 * secQ (and nothing carries the security answer), so screens that show them
 * call this.
 * @returns {Promise<{status:number, message:string, account?:{email:string, emailVerified:boolean, secQ:string, hasSecQA:boolean}}>}
 */
const getMyAccount = async () => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    return { status: pAIdentityResponse.status, message: "Your session expired" };
  }
  const pAIdentity = pAIdentityResponse.identity;
  if (pAIdentity.type != "account") {
    return { status: 400, message: "Only accounts have account details" };
  }
  let response;
  try {
    response = await fetch(app.getAPI_URL() + "/v1/identity", {
      method: "POST",
      body: JSON.stringify({
        idempotencyKey: idem.generateKey("identity"),
        userID: pAIdentity.userID,
        action: "getMyAccount",
      }),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
  } catch (error) {
    console.error(error.message);
    return { status: 500, message: "Error: Unable to load your account details" };
  }
  if (!response.ok) {
    const serverMessage = await readServerMessage(response);
    return {
      status: response.status,
      message: serverMessage || "Error: Unable to load your account details",
    };
  }
  const bodyAsJSON = JSON.parse(await response.text());
  await identityService.updatePlayingAsTokens({
    identityToken: bodyAsJSON.identityToken,
    refreshToken: bodyAsJSON.refreshToken,
  });
  return { status: 200, message: "", account: bodyAsJSON.account };
};

/**
 * @brief returns the email prefs of the playing as identity
 * @return {emailPrefs: {account:boolean, marketing:boolean, match:boolean}}
 */
const getEmailPrefs = () => {
  for (const identity of identityState.cache) {
    if (identity.name === identityState.playingAsName) {
      return identity.emailPrefs;
    }
  }
};

/**
 * Deletes the logged on identit
 * @returns {{status:string, message:string}}
 */
const deleteIdentity = async (currentPassword) => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (pAIdentity.type != "account") {
    return {
      status: 400,
      message: `ERROR: You can only delete an 
      account, but you are playing as a temporary identity`,
    };
  }
  if (!Object.keys(pAIdentity).includes("refreshToken")) {
    return {
      status: 401,
      message:
        "ERROR: Not authorized to make changes to this identity:" +
        pAIdentity.name,
    };
  }

  let response;
  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      userID: pAUserID,
      action: "delete",
      currentPassword,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
  } catch (error) {
    console.log("Error: Unable to delete account");
    console.error(error.message);
    return { status: 500, message: "Error: unable to delete account" };
  }
  if (!response.ok) {
    const serverMessage = await readServerMessage(response);
    const message = serverMessage || "Error: unable to delete the account";
    return { status: response.status, error: message, message };
  }

  if (response.status === 200) {
    // now we remove them from the local cache
    identityState.playingAsName = "notSet";
    let index = 0;
    for (const identity of identityState.cache) {
      if (identity.name === pAIdentity.name) {
        break;
      }
      index++;
    }
    identityState.cache.splice(index, 1);
    return { status: 200, error: "" };
  }
  if (response.status === 401) {
    return { status: 401, error: "Error: Not authorized" };
  }
  if (response.status != 200) {
    return { status: 500, error: "Error: API unable to delete the account" };
  }
};

/**
 * Posts one account recovery step (verify code, verify answer, set password)
 * and reads the reply body, including on errors.
 * @param {{body: object, fallbackMessage: string}}
 * @return {{status:number, message:string, restart:boolean, data:object}}
 * * restart: true when the server says the recovery attempt is over
 *   (expired, too many tries) and the user has to request a new code.
 * * data: the parsed reply body (secQ, recoveryToken, ...)
 */
const postRecoveryStep = async ({ body, fallbackMessage }) => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    return {
      status: pAIdentityResponse.status,
      message: "Your session expired. Please reload the page and try again.",
      restart: false,
      data: {},
    };
  }
  const pAIdentity = pAIdentityResponse.identity;
  let response;
  try {
    response = await fetch(app.getAPI_URL() + "/v1/identity", {
      method: "POST",
      body: JSON.stringify({
        idempotencyKey: idem.generateKey("identity"),
        ...body,
      }),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
  } catch (error) {
    console.log("Error: account recovery request failed: " + body.action);
    console.error(error.message);
    return { status: 500, message: fallbackMessage, restart: false, data: {} };
  }

  let data = {};
  try {
    data = JSON.parse(await response.text());
  } catch (error) {
    data = {};
  }
  const serverMessage =
    typeof data.message === "string" && data.message.length > 0
      ? data.message
      : "";
  return {
    status: response.status,
    message: response.ok ? serverMessage : serverMessage || fallbackMessage,
    restart: data.recoveryRestart === true,
    data,
  };
};

const accountRecoveryPassword = async ({ email, password, recoveryToken }) => {
  if (isPasswordValid(password).status != 200) {
    return { status: 400, message: "Enter a valid password", restart: false };
  }
  const result = await postRecoveryStep({
    body: {
      action: "accountRecoveryPassword",
      email,
      password,
      recoveryToken,
    },
    fallbackMessage: "Error: Unable to set the new password",
  });
  return {
    status: result.status,
    message: result.message,
    restart: result.restart,
  };
};

const isCodeValid = (code) => {
  if (typeof code != "string") {
    return { status: 400, message: "Please enter six numbers" };
  }
  if (/^[0-9]{6}$/.test(code) === false) {
    return { status: 400, message: "Please enter six numbers" };
  }
  return { status: 200, message: "" };
};

/**
 * Account recovery step 2. On success, returns the security question and the
 * one-time recoveryToken the next step needs.
 * @return {{status:number, message:string, restart:boolean, secQ?:string, recoveryToken?:string}}
 */
const verifyAccountRecoveryCode = async ({ email, recoveryCode }) => {
  if (isCodeValid(recoveryCode).status != 200) {
    return { status: 400, message: "Needs to be 6 numbers", restart: false };
  }
  const result = await postRecoveryStep({
    body: { action: "verifyAccountRecoveryCode", email, code: recoveryCode },
    fallbackMessage: "Error: Unable to verify the code",
  });
  if (result.status != 200) {
    return {
      status: result.status,
      message: result.message,
      restart: result.restart,
    };
  }
  return {
    status: 200,
    message: "The code is verified",
    restart: false,
    secQ: typeof result.data.secQ === "string" ? result.data.secQ : "",
    recoveryToken: result.data.recoveryToken,
  };
};

/**
 * Account recovery step 3. On success, returns a new one-time recoveryToken
 * for the password step (the step 2 token stops working).
 * @return {{status:number, message:string, restart:boolean, recoveryToken?:string}}
 */
const verifySecurityAnswer = async ({ email, secA, recoveryToken }) => {
  if (isSecQAValid(secA).status != 200) {
    return {
      status: 400,
      message: "Security answer format is not valid",
      restart: false,
    };
  }
  const result = await postRecoveryStep({
    body: { action: "verifySecurityAnswer", email, secA, recoveryToken },
    fallbackMessage: "Error: Unable to verify the security answer",
  });
  if (result.status != 200) {
    return {
      status: result.status,
      message: result.message,
      restart: result.restart,
    };
  }
  return {
    status: 200,
    message: "The security answer is verified",
    restart: false,
    recoveryToken: result.data.recoveryToken,
  };
};

/**
 * Checks if the code is correct.
 * @param {}
 * @return {{status:number, message: string}}
 */
const verifyCode = async (code) => {
  if (isCodeValid(code).status != 200) {
    return { status: 400, message: "Needs to be 6 numbers" };
  }
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (!Object.keys(pAIdentity).includes("refreshToken")) {
    return {
      statusCode: 401,
      message: "Not authorized to change anything about " + pAIdentity.name,
    };
  }
  let response;
  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      userID: pAUserID,
      action: "verifyCode",
      code: code,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
  } catch (error) {
    console.log("Error: Unable to verifyCode");
    console.error(error.message);
    return { status: 500, message: "Error: Unable to verify the code" };
  }
  if (!response.ok) {
    // e.g. "The code does not match. 4 tries left."
    const serverMessage = await readServerMessage(response);
    return {
      status: response.status,
      message: serverMessage || "The code was incorrect",
    };
  }

  const bodyAsText = await response.text();
  const bodyAsJSON = JSON.parse(bodyAsText);
  const identityToken = bodyAsJSON.identityToken;
  const refreshToken = bodyAsJSON.refreshToken;
  await identityService.updatePlayingAsTokens({ identityToken, refreshToken });
  // should come back as a new claim ,so:
  if (identityToken.identity.emailVerified === true) {
    return { status: 200, message: "" };
  }
  if (identityToken.identity.emailVerified === false) {
    return { status: 400, message: "The code does not match" };
  }
};

const stopSecNag = async () => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (!Object.keys(pAIdentity).includes("refreshToken")) {
    return {
      statusCode: 401,
      message: "Not authorized to change anything about " + pAIdentity.name,
    };
  }
  let response;
  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      userID: pAUserID,
      action: "stopSecNag",
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    console.log("Error: Unable to save stopSecNag");
    console.error(error.message);
    return { status: 500, message: "Error: Unable to save stopSecNag" };
  }

  const bodyAsText = await response.text();
  const bodyAsJSON = JSON.parse(bodyAsText);
  const identityToken = bodyAsJSON.identityToken;
  const refreshToken = bodyAsJSON.refreshToken;
  await identityService.updatePlayingAsTokens({ identityToken, refreshToken });
  // should come back as a new claim ,so:
  if (Object.keys(identityToken.identity).includes("stopSecNag") === false) {
    return { status: 500, message: "Error: Unable to save stopSecNag" };
  }
  if (identityToken.identity.stopSecNag === true) {
    return { status: 200, message: "This will no longer be shown" };
  }
  if (identityToken.identity.stopSecNag === false) {
    return { status: 200, message: "This will continue to be shown " };
  }
};

/**
 * @brief Accepts email address, checks the backend. If an account exists with this email.
 * then it emails a code to that email. However, if no account exstis, then it still
 * tells the user that a code was emailed.
 ** If any previous codes were sent, this replaces them with a new one.
 ** Even if it finds the account, this action does not extend that account's TTL.
 ** This action does not receive back updated tokens for the temporary identity that posts this
 ** Even if the refresh token is valid for the temporary identity, this action does not
 * use it. Therefore, they get one hour from the last identity token they got to do this.
 * @param {{email:string}}
 * @return {{status:number, message: string}}
 */
const accountRecoveryCode = async ({ email }) => {
  let response;
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      action: "sendAccountRecoveryCode",
      email: email,
    };
    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    console.log("Error: Unable to save accountRecoveryCode");
    console.error(error.message);
    //special case: no error logging here
    // but I'll leave this try catch in place anyway, so it doesn't crash
  }

  return {
    status: 200,
    message: "If an account was found with this email, a code was sent to it",
  };
};

/**
 * Emails a code to the playing as identity.
 * @param {{onlyIfNone?: boolean}}
 * * onlyIfNone false (default, the Send New Code button): replaces any
 *   pending code with a new one (at most one a minute).
 * * onlyIfNone true (the automatic send when Verify Email opens): keeps a
 *   pending code that still works, so the code already emailed stays valid.
 * @return {{status:number, message: string}}
 */
const sendEmailCode = async ({ onlyIfNone = false } = {}) => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  if (!Object.keys(pAIdentity).includes("refreshToken")) {
    return {
      statusCode: 401,
      message: "Not authorized to change anything about " + pAIdentity.name,
    };
  }

  let response;

  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      userID: pAUserID,
      action: "sendEmailCode",
      onlyIfNone,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
  } catch (error) {
    console.log("Error: Unable to send the email code");
    console.error(error.message);
    return { status: 500, message: "Error: unable to send the email code" };
  }
  if (!response.ok) {
    // e.g. "A code was sent less than a minute ago..."
    const serverMessage = await readServerMessage(response);
    return {
      status: response.status,
      message: serverMessage || "Error: unable to send the email code",
    };
  }

  const bodyAsText = await response.text();
  const bodyAsJSON = JSON.parse(bodyAsText);
  const identityToken = bodyAsJSON.identityToken;
  const refreshToken = bodyAsJSON.refreshToken;
  // "A code was already sent" (onlyIfNone) comes back without new tokens
  if (identityToken?.identity) {
    await identityService.updatePlayingAsTokens({ identityToken, refreshToken });
  }
  return { status: 200, message: bodyAsJSON.message ?? "" };
};

/**
 * Reads a JSON reply body's message, or "" if there isn't one.
 * @param {Response} response
 * @returns {Promise<string>}
 */
const readServerMessage = async (response) => {
  try {
    const body = JSON.parse(await response.text());
    return typeof body.message === "string" ? body.message : "";
  } catch (error) {
    return "";
  }
};

/**
 * For an existing identity this session controls, saves a new value of one
 * attribute. Only available within this module, so all validation happens
 * before this point. The API also returns new tokens, which this stores.
 * @param {{userID:string, attribute:string, newValue:any, currentPassword?:string}}
 * * currentPassword: required by the server for "email" and "secQA"
 * @returns {{status:number, message:string}} on failure, message is the
 *   server's explanation when it gave one
 */
const storeAttribute = async ({ userID, attribute, newValue, currentPassword }) => {
  const identity = (await getIdentity(userID)).identity;
  if (!Object.keys(identity).includes("refreshToken")) {
    return {
      status: 401,
      message: "Not authorized to change anything about " + identity.name,
    };
  }

  let response;
  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      userID: identity.userID,
      attribute: attribute,
      newValue: newValue,
      type: identity.type,
      action: "updateAttribute",
    };
    if (typeof currentPassword === "string") {
      body.currentPassword = currentPassword;
    }

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(identity.identityToken),
      },
    });
  } catch (error) {
    console.log("Error: Unable to update attribute");
    console.error(error.message);
    return {
      status: 500,
      message: "Error: unable to update attribute",
    };
  }
  if (!response.ok) {
    console.log(`Error: Unable to update attribute: ${response.status}`);
    const serverMessage = await readServerMessage(response);
    return {
      status: response.status,
      message: serverMessage || "Error: unable to update attribute",
    };
  }

  const responseText = await response.text();
  let message = "";
  try {
    const responseBodyAsObj = JSON.parse(responseText);
    // note: This can't use updatePlayingAsTokens, because that sets the playingAs tokens only. It's
    // possible for this to be called by setName. That attribute has a special scenario: In a mode=sameDevice
    // match, the playingAs identity is the owner and creates a temporary identity for playertwo. Therefore,
    // you gotta be able to change the name of playerTwo temp identity (which is not the playing as identity).

    updateIdentityInCacheWToken({
      identity,
      identityToken: responseBodyAsObj.identityToken,
    });
    identity.refreshToken = responseBodyAsObj.refreshToken;
    message = responseBodyAsObj.message;
  } catch (error) {
    console.log("Unable to JSON parse response");
    console.error(error.message);
    return {
      status: 500,
      message: "Error: unable to understand response from the API",
    };
  }
  return { status: 200, message };
};

/**
 * @brief If the refresh token is valid, this will get a new
 * identity token and a new refresh token and set them both into the
 * playing as identity. However, if the refreshToken is not valid,
 * then it will log out the user.
 * @param {refreshToken}
 * @return {{status:number}}
 ** 200 means success
 ** 401 means they ended up getting logged out
 */
export const getNewIdentityToken = async (refreshToken) => {
  if (identityState.sessionInvalidated) {
    return { status: 401 };
  }

  if (identityState.refreshPromise) {
    return identityState.refreshPromise;
  }

  identityState.refreshPromise = (async () => {
    try {
      const response = await fetch(app.getAPI_URL() + "/v1/refresh", {
        method: "POST",
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }

      const responseBodyAsObject = await response.json();

      updatePlayingAsTokens({
        identityToken: responseBodyAsObject.identityToken,
        refreshToken: responseBodyAsObject.refreshToken,
      });

      return { status: 200 };
    } catch (error) {
      // Centralized session invalidation
      identityState.sessionInvalidated = true;

      utilsApp.renderSessionTimeOutPop();

      return { status: 401 };
    } finally {
      identityState.refreshPromise = null;
    }
  })();

  return identityState.refreshPromise;
};

/**
 * after receiving a token from the back, this adds it to the local cache. note: This does not check for
 * duplicates. It just adds it.
 * @param {object} identityToken
 */
const addIdentityToCacheFromToken = (identityToken) => {
  const tokenKeys = Object.keys(identityToken.identity);
  const identity2Add = {};
  for (const key of tokenKeys) {
    identity2Add[key] = identityToken.identity[key];
  }
  identity2Add.identityToken = identityToken;
  identityState.cache.push(identity2Add);
};

/**
 * @brief Updates the playing as identity with newly received identityToken and
 * refreshToken. Tokens must be for the playing as identity.
 * @param {{identityToken: identityToken, refreshToken: refreshToken}}
 * @returns {{status: number, message: string}}
 ** status could be 200 or 400
 ** message explains the problem if it's not 200
 */
export const updatePlayingAsTokens = async ({
  identityToken,
  refreshToken,
}) => {
  console.log("[Token Update] === updatePlayingAsTokens STARTED ===");
  console.log("[Token Update] Timestamp:", new Date().toISOString());
  console.log("[Token Update] Input token info:", {
    hasIdentityToken: !!identityToken,
    hasRefreshToken: !!refreshToken,
    identityTokenType: typeof identityToken,
    refreshTokenType: typeof refreshToken,
    identityTokenKeys: identityToken ? Object.keys(identityToken) : [],
    identityExp: identityToken?.identity?.exp,
    currentTime: dateTimeUtils.epochTime()
  });
  
  // console.log("-----updatePlayingAsTokens:--------");
  // console.log("The timestamp is: " + new Date().toISOString());
  let pAIdentity;
  for (const identity of identityState.cache) {
    if (identity.name === identityState.playingAsName) {
      pAIdentity = identity;
      break;
    }
  }
  
  console.log("[Token Update] Found playing as identity:", {
    name: pAIdentity?.name,
    userID: pAIdentity?.userID,
    hasExistingToken: !!pAIdentity?.identityToken,
    existingTokenType: typeof pAIdentity?.identityToken
  });
  
  if (identityToken.identity.exp <= dateTimeUtils.epochTime()) {
    console.log("[Token Update] ❌ ERROR: identityToken is expired");
    return { status: 400, message: "identityToken is expired" };
  }
  if (refreshToken.exp <= dateTimeUtils.epochTime()) {
    console.log("[Token Update] ❌ ERROR: refreshToken is expired");
    return { status: 400, message: "refreshToken is expired" };
  }
  if (pAIdentity.userID != identityToken.identity.userID) {
    console.log("[Token Update] ❌ ERROR: identityToken userID mismatch");
    return {
      status: 400,
      message: "identityToken userID doesn't match playing as identity",
    };
  }
  if (pAIdentity.userID != refreshToken.userID) {
    console.log("[Token Update] ❌ ERROR: refreshToken userID mismatch");
    return {
      status: 400,
      message: "refreshToken userID doesn't match playing as identity",
    };
  }
  
  console.log("[Token Update] All validations passed, updating tokens...");
  
  const tokenKeys = Object.keys(identityToken.identity);
  for (const key of tokenKeys) {
    pAIdentity[key] = identityToken.identity[key];
  }
  
  console.log("[Token Update] About to overwrite identityToken...");
  console.log("[Token Update] Old token type:", typeof pAIdentity.identityToken);
  console.log("[Token Update] New token type:", typeof identityToken);
  
  pAIdentity.identityToken = identityToken;
  pAIdentity.refreshToken = refreshToken;
  
  console.log("[Token Update] Tokens updated in cache");
  console.log("[Token Update] Updated identity token type:", typeof pAIdentity.identityToken);
  
  const namePart = parseName(pAIdentity.name);
  pAIdentity.pANameHtml = generateNameHtml({
    namePart,
    userID: pAIdentity.userID,
  });
  if (Object.keys(pAIdentity).includes("blurb")) {
    const blurb = pAIdentity.blurb;
    pAIdentity.pANameHtmlBlurb = generateNameHtmlBlurb({
      namePart,
      userID: pAIdentity.userID,
      blurb: pAIdentity.blurb,
    });
  }
  
  console.log("[Token Update] ✅ updatePlayingAsTokens completed successfully");
  return { status: 200, message: "" };
};

/**
 * Input is a live reference to the identity in cache, and the identityToken is intended to be a new
 * token just received from the back end. This will update the identity in cache to match what
 * we got from the back end.
 * @param {{identity: object, identityToken: object}} param0
 */
const updateIdentityInCacheWToken = ({ identity, identityToken }) => {
  const tokenKeys = Object.keys(identityToken.identity);
  for (const key of tokenKeys) {
    identity[key] = identityToken.identity[key];
  }
  identity.identityToken = identityToken;
  return true;
};

/**
 *
 * @returns a copy of the identity state cache
 */
const getCache = () => {
  return JSON.parse(JSON.stringify(identityState.cache));
};

/**
 *
 * @param {[identitiy]} identities
 ** input must be an array of identities
 *
 */
const addIdentities = (identities) => {
  if (identities.isArray === false) {
    return { status: 400, message: "input is not an array" };
  }
  if (identities.length === 0) {
    return { status: 200, message: "" };
  }
  identityState.cache.push(...identities);
};

const dealWithTokens = async ({ identityToken, refreshToken }) => {
  var returnObj = { status: 200 };
  if (identityToken.identity.exp <= dateTimeUtils.epochTime()) {
    // identity token has expired
    const refreshResult =
      await identityService.getNewIdentityToken(refreshToken);
    returnObj.status = refreshResult.status;
  }
  return returnObj;
};

const getLeaderboardTop = async () => {
  // console.log("-----getLeaderboardTop:" + "--------");
  // console.log("The Initial timestamp is: " + new Date().toISOString());

  const pAIdentityResponse = await identityService.getPlayingAs();

  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  let response;
  try {
    const resource = "/v1/identity";
    const body = JSON.stringify({
      idempotencyKey: idem.generateKey("identity"),
      action: "getLeaderboardTop",
    });
    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
      body: body,
    });
    if (!response.ok) {
      console.log(`Error in getLeaderboardTop: ${response.status}`);
    }
    const bodyAsText = await response.text();
    const bodyAsJSON = JSON.parse(bodyAsText);

    if (Object.keys(bodyAsJSON?.identityToken).length > 0) {
      const identityToken = bodyAsJSON.identityToken;
      const refreshToken = bodyAsJSON.refreshToken;
      await identityService.updatePlayingAsTokens({
        identityToken,
        refreshToken,
      });
    }
    return { status: 200, message: "", leaderboard: bodyAsJSON.leaderboard };
  } catch (error) {
    console.error(
      `Error in getLeaderboardTop ${error.status || 500}: ${error.message}`,
    );
    return { status: error.status || 500, message: error.message };
  }
};

/**
 * @brief Tells you if an identity is already in the cache
 * @param {string} userID
 * @returns boolean
 */
const isInCache = (userID) => {
  for (const identity in identityState.cache) {
    if (identity.userID === userID) {
      return true;
    }
  }
  return false;
};

/**
 * The purpose of this is to update any identity in cache with
 * the identity received.
 * @param {*} identity
 */
const updateIdentity = (updatedIdentity) => {
  for (const identity of identityState.cache) {
    if (identity.userID === updatedIdentity.userID) {
      const updatedAttributes = Object.keys(updatedIdentity);
      for (const attribute of updatedAttributes) {
        identity[attribute] = updatedIdentity[attribute];
      }
      break;
    }
  }
};

const getAppStats = async ({ grouping, beginsWith }) => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  const pAUserID = pAIdentity.userID;
  let response;

  try {
    const resource = "/v1/identity";
    const body = {
      idempotencyKey: idem.generateKey("identity"),
      userID: pAUserID,
      action: "getAppStats",
      beginsWith: beginsWith,
      grouping: grouping,
    };

    response = await fetch(app.getAPI_URL() + resource, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        authorization:
          "Bearer " + utilsApp.base64UrlEncode(pAIdentity.identityToken),
      },
    });
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    console.log("Error: Unable to getAppStats");
    console.error(error.message);
    return { status: 500, message: "Error: unable to getAppStats" };
  }

  const bodyAsText = await response.text();
  const bodyAsJSON = JSON.parse(bodyAsText);
  const identityToken = bodyAsJSON.identityToken;
  const refreshToken = bodyAsJSON.refreshToken;
  await identityService.updatePlayingAsTokens({ identityToken, refreshToken });
  const appStats = bodyAsJSON?.appStats ?? [];
  const message =
    bodyAsJSON?.message ?? "No specific message returned from API.";
  return { status: 200, message, appStats };
};

export const identityService = {
  accountRecoveryCode,
  accountRecoveryPassword,
  addIdentities,
  createIdentity,
  dealWithTokens,
  deleteIdentity,
  getAppStats,
  getAnimationMode,
  getEmailPrefs,
  getMyAccount,
  getCache,
  getIdentity,
  getLeaderboardTop,
  getNamePart,
  getNewIdentityToken,
  getPlayingAs,
  gotLoggedOnUser,
  isCodeValid,
  isInCache,
  isEmailTaken,
  isEmailValid,
  isFullNameShapeValid,
  isNameValid,
  isBlurbValid,
  isPasswordValid,
  isSearchValid,
  isSecQAValid,
  isUserIDValid,
  loadIdentity,
  logOn,
  passwordChange,
  parseName,
  parseUserID,
  sendEmailCode,
  setAnimationMode,
  setEmail,
  setEmailPrefs,
  setName,
  setChangedPop,
  setCreatedPop,
  setPlayingAs,
  setPostLogPop,
  setJoinedWatcherPop,
  setJoinedPlayerPop,
  setScheme,
  setSecQA,
  stopSecNag,
  updateIdentity,
  updatePlayingAsTokens,
  verifyAccountRecoveryCode,
  verifySecurityAnswer,
  verifyCode,
};
